import {
  ChipField,
  Datagrid, EditButton, ShowButton, useCanAccess, useRecordContext, useTranslate, CreateButton, TopToolbar, FieldProps,
} from 'react-admin';
import { ListLive } from '@react-admin/ra-realtime';
import * as React from 'react';

import { Button, Chip } from '@mui/material';
import SearchFields from '../components/fields/Filters';
import { PageExplanation } from '../../../components/PageExplanation';
import { FullExportButton } from '../../../utils/customExporter';

/**
 * Displays the phone number type as a translated label inside a centered Chip.
 * @param props - field props.
 *
 */
const PhoneNumberTypeField = (props: FieldProps) => {

  const record = useRecordContext();
  const translate = useTranslate();

  if (!record || !props.source) return null;

  const value = record[props.source];

  if (!value) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Chip
        label={translate(
          `resources.alarm-call-phone-numbers.type.${String(value).toLowerCase()}`,
        )}
        color="primary"
      />
    </div>
  );

};

/**
 * Generic helper field for centering content inside Datagrid cells.
 *
 * ChipField is used intentionally to avoid alignment issues with the
 * default text and number fields. The chip styling is disabled so it
 * renders as plain content.
 * @param props - field props.
 */
const CenteredGridField = (props: FieldProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <ChipField
      {...props}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
      }}
    />
  </div>
);

/**
 * Displays the last time a phone number was called.
 *
 * If value is null, a translated "Never" message is shown.
 * Otherwise, the date and time are displayed in format of two lines.
 * @param props - field props.
 */

export const LastTimeCalledField = (props: FieldProps) => {

  const record = useRecordContext();
  const translate = useTranslate();

  const value = record?.[props.source];

  if (!value) {

    return (
      <div style={{ textAlign: 'center', color: 'grey' }}>
        {translate(
          'resources.alarm-call-phone-numbers.lastTimeCalled.neverCalled',
        )}
      </div>
    );

  }

  const date = new Date(value);

  return (
    <div style={{ textAlign: 'center' }}>
      <div>{date.toLocaleDateString()}</div>
      <div>{date.toLocaleTimeString()}</div>
    </div>
  );

};

/**
 * Renders either an Edit or View button on the phone number entry depending on user permissions.
 *
 * If the user has edit access to the alarm call phone number configuration,
 * an EditButton is shown, otherwise, a ShowButton is rendered.
 */

const EditOrShowButton = () => {

  const record = useRecordContext();

  const { canAccess } = useCanAccess({
    action: 'edit',
    resource: 'alarm-call-phone-number-configs',
  });

  if (!record) return null;

  return canAccess ? (
    <EditButton record={record} />
  ) : (
    <ShowButton record={record} />
  );

};

/**
 * Action button displayed in the list toolbar to navigate
 * to the general customer configuration page.
 */
const GeneralCustomerConfigButton = () => {

  const translate = useTranslate();

  return (
    <Button
      variant="contained"
      size="small"
      onClick={() => {

      }}
    >
      {translate(
        'resources.alarm-call-phone-numbers.text.general_customer_config_button',
      )}

    </Button>
  );

};

export const AlarmCallPhoneNumbersList = () => (
  <>

    <div style={{ marginTop: 30 }}>
      <PageExplanation text="resources.alarm-call-phone-numbers.text.page_info" />
    </div>
    <ListLive
      filters={SearchFields}
      actions={<TopToolbar><GeneralCustomerConfigButton/><CreateButton/><FullExportButton resource="alarm-call-phone-numbers"/></TopToolbar>}
    >
      <Datagrid bulkActionButtons={false}
        sx={{
          '& th': {
            textAlign: 'center',
          },
        }}>
        <CenteredGridField source="phoneNumber" label="resources.alarm-call-phone-numbers.fields.phoneNumber"/>
        <PhoneNumberTypeField source="type" label="resources.alarm-call-phone-numbers.fields.type"/>
        <CenteredGridField source="numberOfAssetGroupsAndAssets"
          label="resources.alarm-call-phone-numbers.fields.numberOfAssetGroupsAndAssets" />
        <CenteredGridField source="timesCalled" label="resources.alarm-call-phone-numbers.fields.timesCalled"/>
        <LastTimeCalledField source="lastTimeCalled" label="resources.alarm-call-phone-numbers.fields.lastTimeCalled"/>
        <EditOrShowButton />
      </Datagrid>
    </ListLive>
  </>
);
